// Learned skills API. Read the active library + pending review
// queue, approve / reject / deprecate from the dashboard. Agent-
// side create / update / share lifecycle goes through the
// `oc-skill` shim — the dashboard never creates skills, only
// reviews and curates them.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

export interface LearnedSkill {
  id: number;
  slug: string;
  title: string;
  description: string;
  body: string;
  author_template: string;
  scope: string;
  status: "active" | "pending_review" | "rejected" | "deprecated";
  secrets_path: string | null;
  review_decided_by: string | null;
  review_decided_at: string | null;
  review_reason: string | null;
  version: number;
  source: string | null;
  assigned_agents: string[] | null;
  created_at: string;
  updated_at: string;
}

interface SkillsResponse {
  skills: LearnedSkill[];
}

export function useActiveSkills(customerId: string | null) {
  return useQuery({
    queryKey: ["skills", "active", customerId],
    enabled: customerId !== null,
    queryFn: ({ signal }) =>
      apiFetch<SkillsResponse>(
        `/api/customers/${customerId}/skills`,
        { signal },
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function usePendingSkills(customerId: string | null, poll?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ["skills", "pending", customerId],
    enabled: customerId !== null,
    queryFn: ({ signal }) =>
      apiFetch<SkillsResponse>(
        `/api/customers/${customerId}/skills/pending`,
        { signal },
      ),
    // Pending queue is operator-facing — refetch fast so a
    // freshly proposed skill shows up promptly.
    staleTime: 10_000,
    refetchInterval: poll?.refetchInterval ?? 15_000,
  });
}

interface MutateArgs {
  customerId: string;
  skillId: number;
}

/** Resolves once the Skills page's list and open detail are read again. */
function invalidateSkills(qc: ReturnType<typeof useQueryClient>, customerId: string): Promise<void> {
  void qc.invalidateQueries({
    queryKey: ["skills", "active", customerId],
  });
  void qc.invalidateQueries({
    queryKey: ["skills", "pending", customerId],
  });
  return invalidateCatalog(qc, customerId);
}

/** The Skills page's list and every open skill's detail (resolves once they are read again). */
export function invalidateCatalog(qc: ReturnType<typeof useQueryClient>, customerId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: ["skills", "catalog", customerId] });
}

export function useApproveSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, skillId }: MutateArgs) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/skills/${skillId}/approve`,
        { method: "POST" },
      ),
    onSuccess: (_, vars) => invalidateSkills(qc, vars.customerId),
  });
}

export function useRejectSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      skillId,
      reason,
    }: MutateArgs & { reason: string }) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/skills/${skillId}/reject`,
        { method: "POST", body: { reason } },
      ),
    onSuccess: (_, vars) => invalidateSkills(qc, vars.customerId),
  });
}

export function useDeprecateSkill() {
  const qc = useQueryClient();
  return useMutation({
    // The screen that runs it shows the error itself; no toast as well.
    meta: { errorHandled: true },
    mutationFn: ({ customerId, skillId }: MutateArgs) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/skills/${skillId}/deprecate`,
        { method: "POST" },
      ),
    onSuccess: (_, vars) => invalidateSkills(qc, vars.customerId),
  });
}

interface UpdateSkillArgs extends MutateArgs {
  title?: string;
  description?: string;
  body?: string;
}

/**
 * Operator-side edit. Inserts a new version of the skill (the
 * old row stays in the table for audit). The router materializer
 * tick rewrites the SKILL.md file in every target agent's home
 * within ~150s. Scope and status never change here — those have
 * their own routes.
 */
export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    // The screen that runs it shows the error itself; no toast as well.
    meta: { errorHandled: true },
    mutationFn: ({
      customerId,
      skillId,
      title,
      description,
      body,
    }: UpdateSkillArgs) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/skills/${skillId}/edit`,
        {
          method: "POST",
          body: { title, description, body },
        },
      ),
    onSuccess: (_, vars) => invalidateSkills(qc, vars.customerId),
  });
}

export function useAssignSkill() {
  const qc = useQueryClient();
  return useMutation({
    // The screen that runs it shows the error itself; no toast as well.
    meta: { errorHandled: true },
    mutationFn: ({
      customerId,
      skillId,
      agents,
      since,
    }: MutateArgs & { agents: string[] | null; since?: string | null }) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/skills/${skillId}/assignments`,
        { method: "POST", body: { agents, ...(since ? { since } : {}) } },
      ),
    // Returned, so the call resolves only once the page holds the new state:
    // a second switch before that would send the old `since` (409).
    onSettled: (_, __, vars) => invalidateSkills(qc, vars.customerId),
  });
}

/** Put a removed skill back (its newest removed version). */
export function useRestoreSkill() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, skillId }: MutateArgs) =>
      apiFetch<LearnedSkill>(`/api/customers/${customerId}/skills/${skillId}/restore`, { method: "POST" }),
    onSuccess: (_, vars) => invalidateSkills(qc, vars.customerId),
  });
}

// ─── the catalog: every skill and who has it (no procedure text) ───

export type SkillCatalog = components["schemas"]["SkillCatalog"];
export type CatalogSkill = components["schemas"]["CatalogSkill"];
export type CatalogAgent = components["schemas"]["CatalogAgent"];
export type CatalogHolder = components["schemas"]["CatalogHolder"];
export type SkillDetail = components["schemas"]["SkillDetail"];
export type CatalogEvent = components["schemas"]["CatalogEvent"];
export type CatalogVersion = components["schemas"]["CatalogVersion"];

export function useSkillCatalog(customerId: string | null) {
  return useQuery({
    queryKey: ["skills", "catalog", customerId],
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<SkillCatalog>(`/api/customers/${customerId}/skills/catalog`, { signal }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useSkillDetail(customerId: string | null, key: string | null) {
  return useQuery({
    queryKey: ["skills", "catalog", customerId, "detail", key],
    enabled: !!customerId && !!key,
    queryFn: ({ signal }) =>
      apiFetch<SkillDetail>(`/api/customers/${customerId}/skills/catalog/${encodeURIComponent(key ?? "")}`, {
        signal,
      }),
    staleTime: 15_000,
    retry: false,
  });
}

/** Give a subagent a skill, or take it away. */
export function useSetSubagentSkill() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, template, slug, on }: { customerId: string; template: string; slug: string; on: boolean }) =>
      apiFetch<{ ok: boolean; via: string }>(
        `/api/customers/${customerId}/subagent-blueprints/${encodeURIComponent(template)}/skills/${encodeURIComponent(slug)}`,
        { method: "PUT", body: { on } },
      ),
    onSettled: (_, __, vars) => {
      void qc.invalidateQueries({ queryKey: ["subagent-blueprints", vars.customerId] });
      void qc.invalidateQueries({ queryKey: ["subagent-blueprint", vars.customerId] });
      return invalidateSkills(qc, vars.customerId);
    },
  });
}

// ─── platform default-skill selection (per-customer opt-out) ───

export interface DefaultSkillSelectionItem {
  slug: string;
  title: string;
  description: string;
  source: string;
  excluded: boolean;
  excludable: boolean;
}

interface DefaultSkillSelectionResponse {
  skills: DefaultSkillSelectionItem[];
  excluded: string[];
}

export function useDefaultSkillSelection(customerId: string | null) {
  return useQuery({
    queryKey: ["default-skills", customerId],
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }) =>
      apiFetch<DefaultSkillSelectionResponse>(
        `/api/customers/${customerId}/default-skills`,
        { signal },
      ),
    staleTime: 10_000,
  });
}

/**
 * Set the full exclusion list for a customer. Toggling a skill off adds its
 * slug; toggling on removes it. The backend re-syncs the container's skills and
 * reloads the gateway, so the change is live after the mutation resolves.
 */
export function useSetDefaultSkillExclusions(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    // The screen that runs it shows the error itself; no toast as well.
    meta: { errorHandled: true },
    mutationFn: (excluded: string[]) =>
      apiFetch<DefaultSkillSelectionResponse>(
        `/api/customers/${customerId}/default-skills`,
        { method: "PUT", body: { excluded } },
      ),
    onSuccess: (data) => {
      qc.setQueryData(["default-skills", customerId], data);
      void qc.invalidateQueries({ queryKey: ["skills", "active", customerId] });
      invalidateCatalog(qc, customerId);
    },
  });
}

// Plain calls for loops (bulk review); the caller invalidates once at the
// end (see invalidateReviewQueues in ./skillWorkshop).

/**
 * `agents`: install for these agents only; null = every agent; left out =
 * whatever the row is assigned to already.
 */
export function approveSkill({ customerId, skillId, agents }: MutateArgs & { agents?: string[] | null }): Promise<LearnedSkill> {
  return apiFetch<LearnedSkill>(`/api/customers/${customerId}/skills/${skillId}/approve`, {
    method: "POST",
    ...(agents !== undefined ? { body: { agents } } : {}),
  });
}

export function rejectSkill({ customerId, skillId, reason }: MutateArgs & { reason: string }): Promise<LearnedSkill> {
  return apiFetch<LearnedSkill>(`/api/customers/${customerId}/skills/${skillId}/reject`, {
    method: "POST",
    body: { reason },
  });
}
